import { useState, useEffect } from "react";
import { Link2, Copy, Check, Calendar, Shield, Loader2, Mail, Users, AlertTriangle } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { getShareUrl } from "@/lib/shareUrl";
import { useUsageLimits } from "@/hooks/useUsageLimits";
import { LimitReached } from "@/components/usage/UpgradePrompt";
import { ExpirationAlert, ExpirationBadge, getExpiredDocuments } from "./ExpirationAlert";

interface LinkedDocument {
  id: string;
  label: string;
  doc_type: string | null;
  document_id: string;
  sort_order: number;
  document: {
    id: string;
    title: string;
    file_name: string;
    file_path: string;
    file_size: number | null;
    mime_type: string;
    expires_at?: string | null;
  };
}

interface ShareDemarcheDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  demarcheTitle: string;
  demarcheId: string;
  linkedDocuments: LinkedDocument[];
}

export function ShareDemarcheDialog({
  open,
  onOpenChange,
  demarcheTitle,
  demarcheId,
  linkedDocuments,
}: ShareDemarcheDialogProps) {
  const [loading, setLoading] = useState(false);
  const [shareUrl, setShareUrl] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [enableExpiration, setEnableExpiration] = useState(false);
  const [expirationDays, setExpirationDays] = useState("7");
  const [permission, setPermission] = useState<"read" | "read_comment">("read");
  const [sendEmail, setSendEmail] = useState(false);
  const [recipientEmail, setRecipientEmail] = useState("");
  const [recipientName, setRecipientName] = useState("");
  const [showExpirationWarning, setShowExpirationWarning] = useState(false);

  const { usage, limits, checkLimit, refreshUsage } = useUsageLimits();
  const canShare = checkLimit("share_links");

  // Check for expired documents
  const expiredDocs = getExpiredDocuments(linkedDocuments);
  const hasExpiredDocs = expiredDocs.length > 0;

  useEffect(() => {
    if (open) {
      refreshUsage();
    }
  }, [open, refreshUsage]);

  const handleGenerateClick = () => {
    if (hasExpiredDocs) {
      setShowExpirationWarning(true);
    } else {
      generateShareLink();
    }
  };

  const generateShareLink = async () => {
    setShowExpirationWarning(false);

    if (!canShare) {
      toast.error("Limite de liens de partage atteinte");
      return;
    }

    setLoading(true);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast.error("Vous devez être connecté");
        return;
      }

      const expiresAt = enableExpiration
        ? new Date(Date.now() + parseInt(expirationDays) * 24 * 60 * 60 * 1000).toISOString()
        : null;

      // Create collection - let DB generate secure hex token via default
      const { data: collection, error: collectionError } = await supabase
        .from('document_collections')
        .insert({
          name: `Dossier: ${demarcheTitle}`,
          description: `Documents de la démarche "${demarcheTitle}"`,
          user_id: user.id,
          permission,
          expires_at: expiresAt,
        })
        .select()
        .single();

      if (collectionError) throw collectionError;

      // Add documents to collection
      const collectionDocs = linkedDocuments.map((doc, index) => ({
        collection_id: collection.id,
        document_id: doc.document_id,
        sort_order: index,
      }));

      const { error: docsError } = await supabase
        .from('collection_documents')
        .insert(collectionDocs);

      if (docsError) throw docsError;

      // Generate URL using DB-generated token
      const url = getShareUrl(`collection/${collection.token}`);
      setShareUrl(url);

      // Send email if enabled
      if (sendEmail && recipientEmail) {
        const { data: profile } = await supabase
          .from('profiles')
          .select('full_name')
          .eq('id', user.id)
          .single();

        await supabase.functions.invoke('notify-share', {
          body: {
            recipientEmail,
            recipientName: recipientName || undefined,
            documentTitle: `Dossier: ${demarcheTitle}`,
            shareUrl: url,
            senderName: profile?.full_name || undefined,
            permission,
            expiresAt: expiresAt || undefined,
            isCollection: true,
            documentCount: linkedDocuments.length,
          },
        });
        toast.success("Lien créé et notification envoyée");
      } else {
        toast.success("Lien de partage créé");
      }

      refreshUsage();
    } catch (error: any) {
      console.error("Error creating share link:", error);
      toast.error("Erreur lors de la création du lien");
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = async () => {
    if (!shareUrl) return;

    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success("Lien copié");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback
      const textarea = document.createElement('textarea');
      textarea.value = shareUrl;
      textarea.style.position = 'fixed';
      textarea.style.left = '-9999px';
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setCopied(true);
      toast.success("Lien copié");
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleClose = () => {
    setShareUrl(null);
    setCopied(false);
    setEnableExpiration(false);
    setExpirationDays("7");
    setPermission("read");
    setSendEmail(false);
    setRecipientEmail("");
    setRecipientName("");
    setShowExpirationWarning(false);
    onOpenChange(false);
  };

  const docCount = linkedDocuments.length;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Link2 className="w-5 h-5 text-primary" />
            Partager le dossier
          </DialogTitle>
          <DialogDescription className="flex items-center gap-2">
            <Users className="w-4 h-4" />
            {docCount} document{docCount > 1 ? 's' : ''} seront partagés
          </DialogDescription>
        </DialogHeader>

        {!shareUrl ? (
          !canShare ? (
            <div className="py-4">
              <LimitReached
                type="share_links"
                current={usage.share_links}
                limit={limits.share_links}
              />
            </div>
          ) : showExpirationWarning ? (
            <div className="py-4 space-y-4">
              <ExpirationAlert
                expiredDocs={expiredDocs}
                onContinue={generateShareLink}
                onCancel={() => setShowExpirationWarning(false)}
                loading={loading}
              />
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  onClick={() => setShowExpirationWarning(false)} 
                  className="flex-1"
                  disabled={loading}
                >
                  Annuler
                </Button>
                <Button 
                  variant="destructive" 
                  onClick={generateShareLink} 
                  className="flex-1"
                  disabled={loading}
                >
                  {loading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    "Partager quand même"
                  )}
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-6 py-4">
              {/* Expired documents warning banner */}
              {hasExpiredDocs && (
                <div className="flex items-center gap-2 p-3 rounded-lg bg-destructive/10 border border-destructive/30">
                  <AlertTriangle className="w-4 h-4 text-destructive shrink-0" />
                  <p className="text-sm text-destructive">
                    <strong>{expiredDocs.length}</strong> document{expiredDocs.length > 1 ? 's' : ''} expiré{expiredDocs.length > 1 ? 's' : ''} dans ce dossier
                  </p>
                </div>
              )}

              {/* Documents preview */}
              <div className="p-3 rounded-lg bg-muted/50 border">
                <p className="text-sm font-medium mb-2">{demarcheTitle}</p>
                <div className="flex flex-wrap gap-1">
                  {linkedDocuments.slice(0, 3).map((doc) => {
                    const isExpired = doc.document.expires_at && 
                      new Date(doc.document.expires_at) < new Date();
                    return (
                      <Badge 
                        key={doc.id} 
                        variant={isExpired ? "destructive" : "outline"} 
                        className="text-xs gap-1"
                      >
                        {isExpired && <AlertTriangle className="w-2.5 h-2.5" />}
                        {doc.label}
                      </Badge>
                    );
                  })}
                  {linkedDocuments.length > 3 && (
                    <Badge variant="secondary" className="text-xs">
                      +{linkedDocuments.length - 3}
                    </Badge>
                  )}
                </div>
              </div>

              {/* Permission */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Shield className="w-4 h-4" />
                  Permission
                </Label>
                <Select value={permission} onValueChange={(v) => setPermission(v as any)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="read">Lecture seule</SelectItem>
                    <SelectItem value="read_comment">Lecture + Commentaires</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Expiration */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label htmlFor="expiration" className="flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    Date d'expiration
                  </Label>
                  <Switch
                    id="expiration"
                    checked={enableExpiration}
                    onCheckedChange={setEnableExpiration}
                  />
                </div>
                {enableExpiration && (
                  <Select value={expirationDays} onValueChange={setExpirationDays}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">1 jour</SelectItem>
                      <SelectItem value="7">7 jours</SelectItem>
                      <SelectItem value="30">30 jours</SelectItem>
                      <SelectItem value="90">90 jours</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              </div>

              {/* Email notification */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label htmlFor="sendEmail" className="flex items-center gap-2">
                    <Mail className="w-4 h-4" />
                    Notifier par email
                  </Label>
                  <Switch
                    id="sendEmail"
                    checked={sendEmail}
                    onCheckedChange={setSendEmail}
                  />
                </div>
                {sendEmail && (
                  <div className="space-y-3">
                    <Input
                      type="email"
                      placeholder="Email du destinataire *"
                      value={recipientEmail}
                      onChange={(e) => setRecipientEmail(e.target.value)}
                    />
                    <Input
                      type="text"
                      placeholder="Nom du destinataire (optionnel)"
                      value={recipientName}
                      onChange={(e) => setRecipientName(e.target.value)}
                    />
                  </div>
                )}
              </div>

              <Button
                onClick={handleGenerateClick}
                disabled={loading || (sendEmail && !recipientEmail)}
                className="w-full"
                variant={hasExpiredDocs ? "destructive" : "default"}
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Génération...
                  </>
                ) : (
                  <>
                    <Link2 className="w-4 h-4 mr-2" />
                    {sendEmail ? "Générer et envoyer" : "Générer le lien"}
                  </>
                )}
              </Button>
            </div>
          )
        ) : (
          <div className="space-y-4 py-4">
            <div className="flex items-center gap-2">
              <Input
                value={shareUrl}
                readOnly
                className="flex-1 font-mono text-sm"
              />
              <Button variant="outline" size="icon" onClick={copyToClipboard}>
                {copied ? (
                  <Check className="w-4 h-4 text-green-500" />
                ) : (
                  <Copy className="w-4 h-4" />
                )}
              </Button>
            </div>
            <p className="text-sm text-muted-foreground">
              {enableExpiration
                ? `Ce lien expire dans ${expirationDays} jour(s).`
                : "Ce lien n'expire pas."}
            </p>
            <p className="text-xs text-muted-foreground">
              Le destinataire pourra voir et télécharger chaque document individuellement.
            </p>
            <div className="flex gap-2">
              <Button variant="outline" onClick={handleClose} className="flex-1">
                Fermer
              </Button>
              <Button onClick={() => setShareUrl(null)} className="flex-1">
                Nouveau lien
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
